import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthShell, AuthHeading } from '@/components/auth/AuthShell';
import { AuthAlert } from '@/components/auth/AuthAlert';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { isNetworkError, NETWORK_ERROR_MESSAGE } from '@/services/network';
import { requestSignInLink } from '@/services/coach-client';
import { appUrl } from '@/lib/basePath';

const NO_ACCOUNT_MESSAGE =
  'We could not find a BMC account for that email. Use the address on your Whop receipt, or create a free account below.';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const resetSuccess = searchParams.get('reset') === 'success';
  // From the coach worker's /go/{code} short link when the code is unknown or expired.
  const linkExpired = searchParams.get('link') === 'expired';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false);
  const [usePassword, setUsePassword] = useState(false);
  const [linkSent, setLinkSent] = useState('');

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLinkSent('');

    const address = email.trim();
    if (!address) { setError('Email is required.'); return; }

    setLoading(true);
    try {
      // Our branded email, sent by the coach worker through Resend. Falls
      // back to Supabase's own email below when the worker can't take it.
      const viaWorker = await requestSignInLink(address);
      if (viaWorker.status === "sent") { setLinkSent(address); return; }
      if (viaWorker.status === "no_account") { setError(NO_ACCOUNT_MESSAGE); return; }
      if (viaWorker.status === "cooldown") {
        setError(`We just sent a link to this email. Check your inbox, or request another in ${viaWorker.retryAfter} seconds.`);
        return;
      }
      if (viaWorker.status === "error") { setError(viaWorker.message); return; }

      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: address,
        options: {
          emailRedirectTo: appUrl('/auth-callback'),
          // Sign-in only. New creators use /signup (email + password, so the
          // same login works in the mobile app); buyers' accounts are created
          // by the purchase webhook. A typo here must not mint an account.
          shouldCreateUser: false,
        },
      });
      if (otpError) {
        setError(
          isNetworkError(otpError)
            ? NETWORK_ERROR_MESSAGE
            : /signups? not allowed|user not found/i.test(otpError.message)
              ? NO_ACCOUNT_MESSAGE
              : otpError.message || 'We could not send your sign-in link. Please try again.',
        );
      } else {
        setLinkSent(address);
      }
    } catch (err: unknown) {
      setError(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setEmailNotConfirmed(false);

    if (!email.trim()) { setError('Email is required.'); return; }
    if (!password) { setError('Password is required.'); return; }

    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (signInError) {
        if (
          signInError.message.toLowerCase().includes('email_not_confirmed') ||
          signInError.message.toLowerCase().includes('email not confirmed')
        ) {
          setEmailNotConfirmed(true);
        } else if (isNetworkError(signInError)) {
          setError(NETWORK_ERROR_MESSAGE);
        } else if (
          signInError.message.toLowerCase().includes('invalid login credentials') ||
          signInError.message.toLowerCase().includes('invalid credentials')
        ) {
          setError('Incorrect email or password. Please try again.');
        } else {
          setError(signInError.message || 'Sign in failed. Please try again.');
        }
      }
      // On success, AuthGate handles navigation
    } catch (err: unknown) {
      setError(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <AuthHeading title="Welcome back" subtitle="Use your existing BMC account to set up and manage Coach." />

      {resetSuccess && (
        <div className="mt-6">
          <AuthAlert variant="success">
            Password updated successfully. Sign in with your new password.
          </AuthAlert>
        </div>
      )}

      {linkExpired && !linkSent && (
        <div className="mt-6">
          <AuthAlert variant="info">
            That sign-in link has expired or was already used. Enter your email below and we'll send you a new one.
          </AuthAlert>
        </div>
      )}

      {linkSent ? (
        <div className="mt-6 flex flex-col gap-4">
          <AuthAlert variant="success">
            Check your inbox. We sent a sign-in link to {linkSent}. Opening it brings you straight back here.
          </AuthAlert>
          <p className="text-[15px] text-white/55">
            The link works once and expires. Check spam and promotions if it has not arrived in a minute.
          </p>
          <Button
            type="button"
            variant="outline"
            fullWidth
            onClick={() => { setLinkSent(''); setError(''); }}
          >
            Use a different email
          </Button>
        </div>
      ) : (
      <form onSubmit={usePassword ? handleSignIn : handleMagicLink} className="mt-6 flex flex-col gap-4">
        <Input
          leftIcon={<Mail size={18} />}
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          autoCapitalize="none"
          inputMode="email"
        />

        {usePassword && (
          <>
            <PasswordInput
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />

            <div className="-mt-1 flex justify-end">
              <Link to="/forgot-password" className="text-sm font-medium text-primary hover:text-primary/80 transition-colors">
                Forgot password?
              </Link>
            </div>
          </>
        )}

        {emailNotConfirmed && (
          <AuthAlert variant="info">
            Please check your inbox and confirm your email address before signing in.
          </AuthAlert>
        )}

        {error && !emailNotConfirmed && <AuthAlert variant="error">{error}</AuthAlert>}

        <Button type="submit" variant="primary" fullWidth loading={loading} className="mt-2">
          {usePassword ? 'Sign In' : 'Email me a sign-in link'}
        </Button>

        <button
          type="button"
          onClick={() => { setUsePassword(!usePassword); setError(''); }}
          className="text-center text-sm font-medium text-primary hover:text-primary/80 transition-colors"
        >
          {usePassword ? 'Email me a link instead' : 'Use a password instead'}
        </button>
      </form>
      )}

      <p className="mt-8 text-center text-[15px] text-white/55">
        New to BMC?{' '}
        <Link to="/signup" className="font-semibold text-primary hover:text-primary/80 transition-colors">
          Create an account
        </Link>
      </p>

      <p className="mt-3 text-center text-[15px] text-white/55">
        Purchased Coach but can’t sign in?{' '}
        <Link to="/access-help" className="font-semibold text-primary hover:text-primary/80 transition-colors">
          Get access help
        </Link>
      </p>
    </AuthShell>
  );
}
